import type { ReactNode } from 'react'
import { useSelector } from 'react-redux'
import * as R from 'ramda'
import { all } from 'ramda'

import HeaderSelectItem, {
  type HeaderSelectItemProps,
} from '~/components/dashboard/header/HeaderSelectItem'
import { useIsAreaIntegrated } from '~/components/dashboard/header/hooks/externalIntegration'
import type SearchContext from '~/constants/searchContext'
import { getCRUDByArea } from '~/store/reducers/auth'
import { getFeatureToggle } from '~/store/reducers/constants'
import type { ContextAvailability } from '~/types'

const allTrue = all(Boolean)

export interface ToolbarContextSelectItemProps extends Omit<
  HeaderSelectItemProps,
  'onSelect'
> {
  availabilityRules: ContextAvailability
  context: string
  label: ReactNode
  onChange: (value: SearchContext) => void
  onOpen: (open: boolean) => void
}

const ToolbarContextSelectItem = ({
  context,
  availabilityRules,
  label,
  value,
  onChange,
  onOpen,
  ...rest
}: ToolbarContextSelectItemProps) => {
  const {
    permission: permissionsArea = null,
    integration: integrationArea = null,
    feature: featureName = null,
    enabledSelector = R.always(true),
  } = availabilityRules || {}
  const contextReadPermissions = useSelector(
    getCRUDByArea(permissionsArea),
  ).read
  const isFeatureEnabled = useSelector(getFeatureToggle(featureName))
  const isSelectorEnabled = useSelector(enabledSelector)
  const contextIntegrationEnabled = useIsAreaIntegrated(integrationArea)
  const contextEnabled = allTrue([
    integrationArea ? contextIntegrationEnabled : true,
    permissionsArea ? contextReadPermissions : true,
    featureName ? isFeatureEnabled : true,
    isSelectorEnabled,
  ])

  if (!contextEnabled) {
    return null
  }

  return (
    <HeaderSelectItem
      active={context === value}
      value={context}
      onBlur={() => onOpen(false)}
      onSelect={(selectedValue) => {
        onChange(selectedValue as SearchContext)
        onOpen(false)
      }}
      {...rest}
    >
      {label}
    </HeaderSelectItem>
  )
}

export default ToolbarContextSelectItem
